import { client, getNewCheckoutPromise, forceResetCart, getCart, addLinesToCart, removeLinesFromCart, createCart } from "../../helpers"
export const cart = {
    state: {
        adding: false,
        client,
        lineItems: [],
        checkout: null,
        checkoutId: null,
        cartId: null,
        itemLimits: [],
        initialized: false, // Add this to track initialization
    },
    reducers: {
        setCheckout(state, checkout) {
            return {
                ...state,
                checkout,
                checkoutId: checkout?.id,
                cartId: checkout?.id,
            }
        },
        setAdding(state, payload) {
            return {
                ...state,
                adding: payload,
            }
        },
        setItemLimits(state, payload) {
            return {
                ...state,
                itemLimits: [...state.itemLimits, ...payload],
            }
        },
        destroyItemLimits(state) {
            return {
                ...state,
                itemLimits: [],
            }
        },
        setInitialized(state, payload) {
            return {
                ...state,
                initialized: payload,
            }
        },
        setInitialized(state, payload) {
            return {
                ...state,
                initialized: payload,
            }
        },
    },
    effects: dispatch => ({
        // Add this initialization effect
        async initializeCart(payload, state) {
            // Skip if already initialized
            if (state.cart.initialized) {
                return;
            }

            // Add safety check for state.cart
            if (!state.cart) {
                const newCheckout = await getNewCheckoutPromise();
                dispatch.cart.setCheckout(newCheckout);
                dispatch.cart.setInitialized(true);
                return;
            }

            const checkoutId = state.cart.checkoutId;

            if (checkoutId) {
                try {
                    // Try to fetch existing checkout
                    const checkout = await client.checkout.fetch(checkoutId);

                    if (checkout && !checkout.completedAt) {
                        // Checkout is valid and not completed
                        dispatch.cart.setCheckout(checkout);
                    } else {
                        // Checkout is completed or invalid, create new one
                        await dispatch.cart.resetCheckout();
                    }
                } catch (error) {
                    // Error fetching checkout, create new one
                    await dispatch.cart.resetCheckout();
                }
            } else {
                // No checkoutId exists, create new checkout
                const newCheckout = await getNewCheckoutPromise();
                dispatch.cart.setCheckout(newCheckout);
            }

            dispatch.cart.setInitialized(true);
        },

        async refetchCheckout(payload, state) {
            let cartId = state.cart.cartId
            try {
                const cart = await getCart(cartId)
                if (!cart) {
                    throw new Error("Cart is invalid, resetting")
                }

                // Remove items that are no longer available
                if (cart?.lines?.edges) {
                    const itemsToRemove = cart.lines.edges
                        .filter(edge => !edge.node.merchandise.availableForSale)
                        .map(edge => edge.node.id)

                    if (itemsToRemove.length) {
                        dispatch.cart.removeLineItems(itemsToRemove)
                        return
                    }
                }

                dispatch.cart.setCheckout(cart)

            } catch (e) {
                console.log(e)
                await dispatch.cart.resetCheckout()
            }
        },

        async addVariantToCart(payload, state) {
            const { shopifyId, quantity, productId } = payload
            dispatch.cart.setAdding(true)

            const lines = [
                { merchandiseId: shopifyId, quantity: parseInt(quantity, 10) },
            ]

            // Get cart and create new if non-existent
            let cartId = state.cart.cartId
            try {
                if (cartId) {
                    const cart = await getCart(cartId)
                    if (!cart) {
                        throw new Error("Cart not found")
                    }
                } else {
                    throw new Error("No cart ID")
                }
            } catch (e) {
                console.log("ERROR: Fetching cart, creating new one", e)
                const newCart = await getNewCheckoutPromise()
                cartId = newCart.id
                dispatch.cart.setCheckout(newCart)
            }

            // Add item to cart
            try {
                const updatedCart = await addLinesToCart(cartId, lines)
                dispatch.cart.setCheckout(updatedCart)
            } catch (error) {
                console.log("CART ERROR:", error)
            } finally {
                dispatch.cart.setAdding(false)
            }

            if (!state.cart.itemLimits.find(obj => obj.id === productId)) {
                dispatch.cart.setItemLimits([{ id: productId, limit: payload.limit }])
            }
        },

        async removeLineItem({ id, variantId, productId }, state) {
            try {
                const updatedCart = await removeLinesFromCart(
                    state.cart.cartId,
                    [id]
                )

                dispatch.cart.removeItemLimit({ variantId, productId })
                dispatch.cart.setCheckout(updatedCart)
            } catch (error) {
                console.log("ERROR removing line item:", error)
            }
        },

        async removeLineItems(ids, state) {
            try {
                const updatedCart = await removeLinesFromCart(
                    state.cart.cartId,
                    ids
                )
                dispatch.cart.setCheckout(updatedCart)
            } catch (error) {
                console.log("ERROR removing line items:", error)
            }
        },

        async resetCheckout(payload, state) {
            forceResetCart()
            const cart = await getNewCheckoutPromise()
            console.log("Resetting cart : ", cart)
            return {
                ...state,
                checkout: cart,
                checkoutId: cart.id,
                cartId: cart.id,
                lineItems: [],
                adding: false,
                itemLimits: [],
            }
        },

        async resetItemLimits(state) {
            dispatch.cart.destroyItemLimits()
        },

        async removeItemLimit({ variantId, productId }, state) {
            function removeObjectById(id, array) {
                const index = array.findIndex(obj => obj.id === id)
                if (index !== -1) {
                    array.splice(index, 1)
                }
                return array
            }

            // check if there are other variants with same id
            const lineItems = state.cart.checkout?.lines?.edges?.map(edge => edge.node) || []
            const found = lineItems.filter(
                i => i.merchandise.product.id == productId
            )
            if (!found.length) {
                const itemLimits = removeObjectById(productId, state.cart.itemLimits)
                dispatch.cart.setItemLimits(itemLimits)
            }
        },

        async clearCart(lineItems, state) {
            let updatedCart

            try {
                updatedCart = await removeLinesFromCart(
                    state.cart.cartId,
                    lineItems
                )
            } catch (e) {
                // If the remove line failed, reset the cart and try again
                await dispatch.cart.resetCheckout()
                updatedCart = await removeLinesFromCart(
                    state.cart.cartId,
                    lineItems
                )
            }
            dispatch.cart.setCheckout(updatedCart)
            dispatch.cart.resetItemLimits()
        },
    }),
}